/**
 * TaxonomyChip — single chip pill, used by FilterBar today and the
 * wizard chip picker / side-panel chip strip in slice 5.
 *
 * Color contract (wireframe D1/D2, PR #46):
 *   - Active:   solid colored background, dark text. Border collapses
 *               into bg. Reads as "selected".
 *   - Inactive: SOLID muted version of the chip color (≈ 22% of the
 *               saturated swatch, mixed with white). Reads as "this
 *               filter is OFF but the category is still on the map".
 *               Replaces the prior white-background-with-ring look,
 *               which the client called out as "too pastel" in the
 *               2026 May wireframes.
 *
 * The palette was locked in /plan-design-review for WCAG-AA against
 * #1F1F1F dark text — so we don't need to compute a contrasting fg
 * per-chip. If the palette is ever extended, re-run the contrast pass.
 */

import React from "react";
import "./TaxonomyChip.css";

// Mix a hex color toward white. ratio=0 returns the original color,
// ratio=1 returns pure white. We use ~0.78 for inactive chips so the
// swatch reads as a related-but-muted version of the active color.
//
// Hand-rolled (no chroma-js dep) because we already ship per-chip
// inline styles — keeping the helper here keeps the dependency surface
// the same and makes the math reviewable inline.
function mixWithWhite(hex, ratio) {
  if (!hex || typeof hex !== "string") return "#FFFFFF";
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return "#FFFFFF";
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return "#FFFFFF";
  const mix = (channel) =>
    Math.round(channel + (255 - channel) * ratio);
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

// Catch-all "Other"/"Unspecified" buckets render as a BLACK square on the
// map (see MARKER_NEUTRAL_CURRENT), not their light-grey seed color. Item 2
// (client 2026-06-18) wants the SELECTED pill to match that square, so an
// active catch-all chip is forced to black bg + white text.
const CATCH_ALL_ACTIVE_BG = "#000000";

export default function TaxonomyChip({
  name,
  color,
  isActive,
  onClick,
  disabled = false,
  size = "md",
  ariaLabel,
  isCatchAll = false,
}) {
  const handleKey = (e) => {
    if (disabled || !onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  // Inline styles for the swatch color so we don't ship a stylesheet
  // entry per chip — there are 14 by default and admins can add more.
  //
  // Inactive uses a same-hue tint (78% toward white) instead of pure
  // white. The colored ring is kept so the boundary is unambiguous
  // even when the muted fill and the strip background are similar
  // values.
  //
  // Catch-all override (item 2): when SELECTED, paint black so the pill
  // matches the black map square. Inactive catch-all keeps the normal
  // muted-tint look so the off state is consistent with every other chip.
  const catchAllActive = isCatchAll && isActive;
  const inactiveFill = mixWithWhite(color, 0.78);
  const style = catchAllActive
    ? { backgroundColor: CATCH_ALL_ACTIVE_BG, borderColor: CATCH_ALL_ACTIVE_BG }
    : {
        backgroundColor: isActive ? color : inactiveFill,
        borderColor: color,
      };

  return (
    <span
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-pressed={onClick ? !!isActive : undefined}
      aria-label={ariaLabel || name}
      aria-disabled={disabled || undefined}
      className={[
        "taxonomy-chip",
        `taxonomy-chip--${size}`,
        isActive ? "taxonomy-chip--active" : "",
        catchAllActive ? "taxonomy-chip--catch-all-active" : "",
        disabled ? "taxonomy-chip--disabled" : "",
        onClick ? "taxonomy-chip--interactive" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKey}
    >
      {name}
    </span>
  );
}
